import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useAppDispatch } from "@app/hooks"
import { updateSettings } from "@features/lead-routing/leadRouting.slice"
import { RoutingSettings } from "@features/lead-routing/leadRouting.types"

// The balancing controls for lead distribution, in one place. Both tune how the
// engine spreads leads across a rule's consultants; neither touches who is
// eligible.
export function SettingsDialog(props: {
  open: boolean
  settings: RoutingSettings | undefined
  onClose: () => void
}) {
  const dispatch = useAppDispatch()
  const [windowDays, setWindowDays] = useState("1")
  const [maxInARow, setMaxInARow] = useState("2")
  const [saving, setSaving] = useState(false)

  // Seed the fields from the live settings each time the dialog opens.
  useEffect(() => {
    if (props.open && props.settings) {
      setWindowDays(String(props.settings.windowDays))
      setMaxInARow(String(props.settings.maxInARow))
    }
  }, [props.open, props.settings])

  const windowNum = Number(windowDays)
  const maxNum = Number(maxInARow)
  const windowValid = Number.isInteger(windowNum) && windowNum >= 1
  const maxValid = Number.isInteger(maxNum) && maxNum >= 1
  const canSave = windowValid && maxValid && !saving

  const save = async () => {
    setSaving(true)
    try {
      await dispatch(
        updateSettings({ windowDays: windowNum, maxInARow: maxNum }),
      ).unwrap()
      props.onClose()
    } catch {
      // errors surface via the snackbar dispatched inside the thunk
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Routing settings</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <TextField
              label="Balance over (days)"
              type="number"
              size="small"
              value={windowDays}
              onChange={(e) => setWindowDays(e.target.value)}
              error={windowDays !== "" && !windowValid}
              inputProps={{ min: 1 }}
              InputLabelProps={{ sx: { textTransform: "none" } }}
              sx={{ width: 160 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, textTransform: "none" }}
            >
              How far back fairness looks. Lower keeps each day even; higher
              smooths over the week but lets a consultant who was off accrue a
              bigger catch-up.
            </Typography>
          </Box>
          <Box>
            <TextField
              label="Max leads in a row"
              type="number"
              size="small"
              value={maxInARow}
              onChange={(e) => setMaxInARow(e.target.value)}
              error={maxInARow !== "" && !maxValid}
              inputProps={{ min: 1 }}
              InputLabelProps={{ sx: { textTransform: "none" } }}
              sx={{ width: 160 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, textTransform: "none" }}
            >
              Never hand one consultant more than this many leads back to back,
              so any catch-up is spread out rather than arriving as a burst.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={!canSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
